import { useState, useEffect, useRef, useCallback } from 'react';
import { DoorOpen, Sparkles, Loader2, X } from 'lucide-react';
import {
  SettingsButton,
  SlideSettingsDrawer,
  DrawerSection,
  DrawerTextarea,
  DrawerCheckbox,
  DrawerOption,
  DrawerNote,
} from '../shared/SlideShared';
import type { EmbedEventHandlers } from '../shared/SlideShared';
import { BufferedFrame } from '../shared/BufferedFrame';
import { isEmbedMessage, EmbedEventPayload } from '../../../../types';
import { getDoor, DOOR_WORLDS } from '../../../../lib/doors/doors';
import { consumePendingBuild } from '../../../../lib/doors/pendingBuild';
import {
  startDoorRun,
  cancelDoorRun,
  clearDoorRun,
  getDoorRun,
  watchDoorRun,
  type DoorRunState,
} from '../../../../lib/doors/buildRuns';
import {
  GeneratedInteractiveSlideData,
  Theme,
  ThemeId,
  themes,
} from '../slideTypes';

/**
 * Synthetic "url" recorded on embed_interactions rows for a generated
 * interactive — there is no external URL (the HTML is sealed into the slide),
 * but the tracking pipeline keys events by (slideId, url).
 */
const doorEmbedUrl = (data: GeneratedInteractiveSlideData) => `kula-door:${data.door}`;

/** "1m 24s" / "48s" — a build runs for minutes, and a wait with no clock on it
 *  is the thing authors read as a hang. */
function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}

/**
 * Activity Door build — renders the sealed self-contained HTML in a sandboxed
 * iframe (srcDoc, allow-scripts only: the build runs, but gets no origin, no
 * storage, no network identity). Capture follows the webapp embed bridge: the
 * generated HTML posts `lms-embed-event` messages to its parent, which this
 * component forwards to `embedHandlers` → embed_interactions. Because a
 * srcDoc sandbox has an opaque origin, messages are validated by SOURCE
 * (event.source === our iframe's contentWindow), not by origin.
 *
 * In the builder, the stub state carries the build studio's core loop:
 * Build (streams the generation into a live preview) → play the result →
 * Tweak (refine rounds via the settings drawer).
 */
export function GeneratedInteractiveSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  slideId,
  embedHandlers,
  lessonSource,
}: {
  data: GeneratedInteractiveSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: GeneratedInteractiveSlideData) => void;
  slideId?: string;
  lessonId?: string;
  courseId?: string;
  embedHandlers?: EmbedEventHandlers;
  /** Readable text of the lesson slides before this one (builder-supplied). */
  lessonSource?: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const door = getDoor(data.door);

  // ---- Build state (builder-only) ----
  // The run itself lives in lib/doors/buildRuns, NOT here: this component is
  // unmounted by the Edit|Preview toggle and by every slide change, and a
  // six-minute build must survive both. What is local is only what is local to
  // this view of it.
  const [refineText, setRefineText] = useState('');
  const [changeNote, setChangeNote] = useState<string | null>(null);
  // A world picked on an already-built door, awaiting the author's go-ahead to
  // restyle. Local on purpose: until they press Restyle nothing has happened,
  // and once they do the run (which survives unmount) carries the target.
  const [pendingWorld, setPendingWorld] = useState<ThemeId | null>(null);
  const [run, setRun] = useState<DoorRunState | undefined>(() =>
    slideId ? getDoorRun(slideId) : undefined,
  );
  const appliedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!slideId) return;
    setRun(getDoorRun(slideId));
    return watchDoorRun(slideId, setRun);
  }, [slideId]);

  const running = run?.status === 'running';
  const refining = running && run.mode === 'refine';
  const restyling = running && run.mode === 'restyle';
  const buildError = run?.status === 'error' ? run.error ?? null : null;
  const noFitVerdict = run?.status === 'no-fit' ? run.verdict ?? null : null;

  // Re-render once a second while a run is live, so the elapsed clock moves
  // even through Opus's opening think — when nothing else changes for minutes.
  const [, setElapsedTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setElapsedTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  // A finished run is applied to the slide by whoever is mounted when it lands
  // — or, if nobody was, by the next mount. `token` makes that exactly-once.
  //
  // "Whoever" means an EDITING mount. The builder's Preview (and the student
  // viewer) render this slide with a no-op `onUpdate`, and applying there used
  // to call that no-op and then clear the run — a nine-minute, ~1,100-credit
  // build discarded because the author pressed Preview to check on it, with
  // the slide falling back to "Briefed — not built yet" and no error anywhere.
  // A non-editing mount shows the result (below) and leaves it in the registry
  // for the Edit-mode mount to write.
  useEffect(() => {
    if (!isEditing) return;
    if (!slideId || run?.status !== 'done' || !run.result) return;
    if (appliedRef.current === run.token) return;
    appliedRef.current = run.token;
    const { html, note, model } = run.result;
    if (run.mode === 'restyle') {
      // The one place a built door's world changes: the HTML that now carries
      // it has landed, so the setting finally tells the truth.
      onUpdate({
        ...data,
        html,
        intake: run.world ? { ...data.intake, world: run.world as ThemeId } : data.intake,
        buildMeta: {
          model,
          builtAt: new Date().toISOString(),
          refinementCount: (data.buildMeta?.refinementCount ?? 0) + 1,
        },
      });
      setChangeNote(note ?? 'Restyled.');
    } else if (run.mode === 'refine') {
      onUpdate({
        ...data,
        html,
        buildMeta: {
          model,
          builtAt: new Date().toISOString(),
          refinementCount: (data.buildMeta?.refinementCount ?? 0) + 1,
        },
      });
      setChangeNote(note ?? 'Updated.');
      setRefineText('');
    } else {
      onUpdate({
        ...data,
        html,
        receipt: note ?? undefined,
        buildMeta: { model, builtAt: new Date().toISOString(), refinementCount: 0 },
      });
    }
    clearDoorRun(slideId);
  }, [run, slideId, data, onUpdate, isEditing]);

  // What to play: the saved build, or — in Preview, before an editing mount has
  // written it — the finished result still waiting in the registry.
  const pendingHtml = !isEditing && run?.status === 'done' ? run.result?.html : undefined;
  const html = data.html || pendingHtml;

  useEffect(() => {
    if (isEditing || !html) return;

    const handleMessage = (event: MessageEvent) => {
      // Sealed srcDoc sandbox → opaque origin ("null"); validate by source.
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      if (!isEmbedMessage(event.data)) return;

      const payload = event.data.payload as EmbedEventPayload;
      const id = slideId || 'unknown';
      const url = doorEmbedUrl(data);

      switch (payload.event) {
        case 'ready':
          // Sealed builds need no lms-init handshake — nothing to configure.
          break;
        case 'started':
          embedHandlers?.onEmbedEvent?.(id, url, { status: 'started' });
          break;
        case 'progress':
          embedHandlers?.onEmbedEvent?.(id, url, {
            status: 'in_progress',
            progress: payload.percent,
          });
          break;
        case 'completed':
          embedHandlers?.onEmbedEvent?.(id, url, {
            status: 'completed',
            progress: 100,
            score: payload.score,
          });
          break;
        case 'advance':
          embedHandlers?.onEmbedAdvance?.(id);
          break;
        case 'submitted':
          embedHandlers?.onEmbedEvent?.(id, url, {
            submissionData: payload.submission.content,
            submissionType: payload.submission.type,
          });
          break;
        case 'error':
          console.error('Generated interactive error:', payload.message);
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [data, html, isEditing, slideId, embedHandlers]);

  const updateIntake = (patch: Partial<GeneratedInteractiveSlideData['intake']>) =>
    onUpdate({ ...data, intake: { ...data.intake, ...patch } });

  const handleBuild = useCallback(() => {
    if (!slideId) return;
    startDoorRun(slideId, {
      mode: 'build',
      door: data.door,
      intake: {
        context: data.intake.context,
        goal: data.intake.goal,
        length: data.intake.length,
        world: data.intake.world,
      },
      lessonSource: data.intake.useLessonSource ? lessonSource : undefined,
    });
  }, [slideId, data, lessonSource]);

  const handleRefine = useCallback(() => {
    if (!slideId || !data.html || !refineText.trim()) return;
    setChangeNote(null);
    startDoorRun(slideId, {
      mode: 'refine',
      previousHtml: data.html,
      refinementText: refineText.trim(),
    });
  }, [slideId, data.html, refineText]);

  /**
   * The World picker. Before a build, a world is just part of the brief and is
   * stored as before. AFTER a build it is baked into the HTML, so storing the
   * setting alone changed nothing on screen; instead it asks to restyle.
   */
  const handlePickWorld = (worldId: ThemeId) => {
    if (!data.html) {
      updateIntake({ world: worldId });
      return;
    }
    if (running) return;
    // Back to the saved world while another is on offer: the author is
    // backing out, not asking for anything.
    if (pendingWorld && worldId === data.intake.world) return setPendingWorld(null);
    if (worldId === pendingWorld) return setPendingWorld(null);
    // Otherwise offer the restyle, INCLUDING into the saved world. The saved
    // world is not proof of the build's look: before restyling existed the
    // picker stored a world after the build without touching the HTML, so a
    // door can say Terra and look like Hyperpop, and refusing the saved world
    // left no way to make it true.
    setPendingWorld(worldId);
  };

  const handleRestyle = useCallback(() => {
    if (!slideId || !data.html || !pendingWorld) return;
    setChangeNote(null);
    startDoorRun(slideId, { mode: 'restyle', previousHtml: data.html, world: pendingWorld });
    setPendingWorld(null);
  }, [slideId, data.html, pendingWorld]);

  // The world the picker shows: where a restyle in flight is going, else the
  // one the author is about to confirm, else the saved one. A cancelled or
  // failed restyle drops the run, so the picker falls back by itself.
  const shownWorld: ThemeId =
    (restyling && (run?.world as ThemeId | undefined)) || pendingWorld || data.intake.world;

  // Auto-start the build handed off from the intake (one-shot — consuming
  // removes the mark, so cancels and errors land on the stub rather than
  // rebuilding on sight). StrictMode's dev double-mount needs no special
  // handling now: the first mount consumes the mark and starts the run, the
  // unmount no longer aborts it, and the surviving mount picks it up by
  // subscription.
  useEffect(() => {
    if (!isEditing || data.html || !slideId) return;
    if (consumePendingBuild(slideId)) handleBuild();
  }, [isEditing, data.html, slideId, handleBuild]);

  const settingsDrawer = isEditing && (
    <SlideSettingsDrawer
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title={`${door.name} — brief`}
    >
      <DrawerSection label="Context">
        <DrawerTextarea
          rows={4}
          value={data.intake.context}
          onChange={(e) => updateIntake({ context: e.target.value })}
          placeholder={door.contextPlaceholder}
        />
      </DrawerSection>
      <DrawerSection label="What it should achieve">
        <DrawerTextarea
          rows={2}
          value={data.intake.goal}
          onChange={(e) => updateIntake({ goal: e.target.value })}
          placeholder={door.goalPlaceholder}
        />
      </DrawerSection>
      <DrawerSection label="Source">
        <DrawerCheckbox
          id="door-use-lesson-source"
          checked={data.intake.useLessonSource}
          onChange={(v) => updateIntake({ useLessonSource: v })}
          label="Use the lesson slides before this as source material"
        />
      </DrawerSection>
      <DrawerSection label="Length">
        <div style={{ display: 'flex', gap: 6 }}>
          <DrawerOption selected={data.intake.length === 'short'} onClick={() => updateIntake({ length: 'short' })}>
            Short
          </DrawerOption>
          <DrawerOption selected={data.intake.length === 'long'} onClick={() => updateIntake({ length: 'long' })}>
            Long
          </DrawerOption>
        </div>
      </DrawerSection>
      <DrawerSection label="World">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {DOOR_WORLDS.map((worldId: ThemeId) => (
            <DrawerOption
              key={worldId}
              selected={shownWorld === worldId}
              onClick={() => handlePickWorld(worldId)}
            >
              {themes[worldId].name}
            </DrawerOption>
          ))}
        </div>
        {data.html && pendingWorld && !running && (
          <div style={{ marginTop: 8 }}>
            <p style={{ margin: '0 0 6px', fontSize: 11.5, color: 'var(--ink-3)' }}>
              {pendingWorld === data.intake.world
                ? `If this build doesn't look like ${themes[pendingWorld].name}, this rewrites it in that look. `
                : 'The world is baked into the build, so changing it rewrites the whole file in the new look. '}
              Content, beats and feedback stay as they are. Uses credits, about as many as the
              original build.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={handleRestyle}
                style={{
                  padding: '6px 12px',
                  fontSize: 12.5,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  background: 'var(--accent)',
                  color: '#fff',
                }}
              >
                Restyle in {themes[pendingWorld].name}
              </button>
              <button
                onClick={() => setPendingWorld(null)}
                style={{
                  padding: '4px 8px',
                  fontSize: 11.5,
                  fontFamily: 'inherit',
                  borderRadius: 5,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--ink-3)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {restyling && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--ink-3)' }} />
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              Restyling in {run?.world && run.world in themes ? themes[run.world as ThemeId].name : 'the new world'}…
            </span>
            {/* Same reason as the tweak's Cancel: a restyle keeps the build on
                screen, so there is no overlay to cancel from, and unmounting
                no longer aborts. */}
            <button
              onClick={() => slideId && cancelDoorRun(slideId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 8px',
                fontSize: 11.5,
                fontFamily: 'inherit',
                borderRadius: 5,
                border: 'none',
                background: 'transparent',
                color: 'var(--ink-3)',
                cursor: 'pointer',
              }}
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        )}
      </DrawerSection>
      {data.html && (
        <DrawerSection label="Tweak it">
          <DrawerTextarea
            rows={2}
            value={refineText}
            onChange={(e) => setRefineText(e.target.value)}
            placeholder="Make the reveal land harder… the feedback is too soft… more serious…"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <button
              onClick={handleRefine}
              disabled={running || !refineText.trim()}
              style={{
                padding: '6px 12px',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: 'inherit',
                borderRadius: 6,
                border: 'none',
                cursor: running || !refineText.trim() ? 'default' : 'pointer',
                background: running || !refineText.trim() ? 'var(--line)' : 'var(--accent)',
                color: running || !refineText.trim() ? 'var(--ink-3)' : '#fff',
              }}
            >
              {refining ? 'Tweaking…' : 'Tweak'}
            </button>
            {refining && (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--ink-3)' }} />
                {/* A tweak keeps the existing build on screen, so it never gets the
                    build overlay — and therefore never got that overlay's Cancel.
                    It needs its own: unmounting no longer aborts anything, so
                    without this there is NO way to stop a tweak, and an abandoned
                    one still lands on the slide (and counts a refinement round)
                    the next time the author opens it. */}
                <button
                  onClick={() => slideId && cancelDoorRun(slideId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    fontSize: 11.5,
                    fontFamily: 'inherit',
                    borderRadius: 5,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--ink-3)',
                    cursor: 'pointer',
                  }}
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              </>
            )}
            {changeNote && !running && (
              <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>✓ {changeNote}</span>
            )}
          </div>
        </DrawerSection>
      )}
      {data.receipt && (
        <DrawerNote title="Kula invented">
          <p style={{ margin: 0 }}>
            {data.receipt} — check these ring true for your context.
          </p>
        </DrawerNote>
      )}
      {buildError && (
        <DrawerNote title="Something went wrong">
          <p style={{ margin: 0 }}>{buildError}</p>
        </DrawerNote>
      )}
    </SlideSettingsDrawer>
  );

  // Building state — paint the experience as it materialises. A refine over an
  // existing build keeps that build on screen (the drawer shows "Tweaking…"):
  // there is something to look at, and replacing it with a spinner would hide
  // the very thing the author is describing changes to.
  if (running && run && (run.mode === 'build' || !data.html)) {
    const elapsed = Math.max(0, Math.round((Date.now() - run.startedAt) / 1000));
    const thinking = run.phase === 'thinking';
    // Two numbers, never both: characters of reasoning before the document
    // starts, characters of document after. Either one moving is the proof
    // that the build is alive — which is exactly what was missing.
    const measure = thinking
      ? run.thoughtChars > 0 && `${Math.max(1, Math.round(run.thoughtChars / 1000))}k thought through`
      : run.writtenChars > 0 && `${Math.max(1, Math.round(run.writtenChars / 1000))}k written`;

    return (
      <div className="relative h-full" style={{ backgroundColor: theme.colors.surface }}>
        {run.preview ? (
          // Double-buffered: a plain srcDoc iframe reloads (paints white) on
          // every repaint, and the preview repaints every couple of seconds
          // for the whole write phase — a flash the author reads as broken.
          <BufferedFrame
            doc={run.preview}
            title="Building…"
            className="pointer-events-none"
            sandbox="allow-scripts"
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent, #D6451F)' }} />
            <p className="text-sm" style={{ color: theme.colors.textMuted }}>
              {thinking
                ? `kula is working out how to stage your ${door.name.toLowerCase()}…`
                : `kula is staging your ${door.name.toLowerCase()}…`}
            </p>
            {thinking && (
              <p className="text-xs max-w-sm" style={{ color: theme.colors.textMuted }}>
                It thinks the whole thing through before it writes a line, so nothing
                appears here for the first few minutes.
              </p>
            )}
          </div>
        )}
        <div
          className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-4 px-4 py-2 text-xs"
          style={{ background: 'rgba(0,0,0,0.75)', color: '#fff' }}
        >
          <span className="flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" />
            Building · {formatElapsed(elapsed)}
            {measure && ` · ${measure}`}
          </span>
          <span className="hidden sm:inline opacity-70">
            Keep working — moving around the lesson won't stop this.
          </span>
          <button
            onClick={() => slideId && cancelDoorRun(slideId)}
            className="flex items-center gap-1 hover:opacity-80 shrink-0"
            style={{ color: '#fff' }}
          >
            <X className="w-3 h-3" /> Cancel
          </button>
        </div>
      </div>
    );
  }

  // Built state — play the sealed interactive.
  if (html) {
    return (
      <div className="relative h-full group">
        <iframe
          ref={iframeRef}
          srcDoc={html}
          title={`${door.name} interactive`}
          className="w-full h-full border-0"
          sandbox="allow-scripts"
        />
        {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
        {settingsDrawer}
      </div>
    );
  }

  // Stub state — briefed, awaiting its build.
  const briefed = data.intake.context.trim().length > 0;
  const worldTheme = themes[data.intake.world];
  return (
    <div className="relative h-full group" style={{ backgroundColor: theme.colors.surface }}>
      <div className="h-full flex flex-col items-center justify-center gap-4 px-8 text-center">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent, #D6451F) 10%, transparent)' }}
        >
          <DoorOpen className="w-8 h-8" style={{ color: 'var(--accent, #D6451F)' }} />
        </div>
        <div>
          <p className="font-medium text-base mb-1" style={{ color: theme.colors.text }}>
            {door.name}
          </p>
          <p className="text-sm max-w-md" style={{ color: theme.colors.textMuted }}>
            {briefed ? data.intake.context : door.tagline}
          </p>
        </div>
        <span
          className="text-xs px-2.5 py-1 rounded-full border"
          style={{
            color: worldTheme.colors.text,
            backgroundColor: worldTheme.colors.background,
            borderColor: worldTheme.colors.primary,
          }}
        >
          {worldTheme.name}
        </span>
        {noFitVerdict && (
          <p
            className="text-sm max-w-md px-4 py-3 rounded-lg border text-left"
            style={{
              color: theme.colors.text,
              borderColor: 'var(--accent, #D6451F)',
              backgroundColor: 'color-mix(in srgb, var(--accent, #D6451F) 6%, transparent)',
            }}
          >
            <span className="font-medium">The door pushed back:</span> {noFitVerdict}
          </p>
        )}
        {buildError && !noFitVerdict && (
          <p className="text-sm max-w-md" style={{ color: '#b91c1c' }}>
            {buildError}
          </p>
        )}
        {isEditing && (
          <div className="flex items-center gap-3">
            {briefed && (
              <button
                onClick={handleBuild}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                style={{ backgroundColor: 'var(--accent, #D6451F)' }}
              >
                <Sparkles className="w-4 h-4" />
                {noFitVerdict || buildError ? 'Build again' : 'Build it'}
              </button>
            )}
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors border"
              style={{
                color: theme.colors.text,
                borderColor: theme.colors.textMuted,
                backgroundColor: 'transparent',
              }}
            >
              {briefed ? 'Edit brief' : 'Write the brief'}
            </button>
          </div>
        )}
        {!isEditing && (
          <p className="text-xs" style={{ color: theme.colors.textMuted }}>
            {briefed ? 'Briefed — not built yet.' : 'No brief yet.'}
          </p>
        )}
      </div>
      {settingsDrawer}
    </div>
  );
}
